import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Popconfirm, Row, Select, Space, Table, Tag } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ModalTextForm from '../../components/modal/ModalTextForm';
import { khoiTaoLopDaoTaoKeHoach, luuDangKyDaoTaoBoSung, xoaDangKyDaoTaoBoSung } from '../../services/daotao';
import { timKiemDSNhanVien } from '../../services/nhanvien';
import { TimKiemNhanVienRequest } from '../../models/nhanvien/NhanVien';
import { useDispatch } from 'react-redux';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { trangThaiDangKyDaoTao, trangThaiLopDaoTao, trangThaiTongHopDangKyDaoTao } from '../../constants';
import TrangThaiLopDaoTaoTag from '../daotao/TrangThaiLopDaoTaoTag';
import NhanVienSelectBox from '../utils/NhanVienSelectBox';
import { Link } from "react-router-dom";
import { Links } from '../../helpers/Links';

interface Props {
    data: any[];
    trangThai: number;
    idDangKyDaoTaoNam: number;
    isEditable: boolean;
    onReload: () => void;
    /** người duyệt cuối cùng: hiện ô chọn ở từng nhân viên chờ duyệt để Duyệt / Từ chối */
    coTheDuyet?: boolean;
    /** id chi tiết (từng nhân viên) đang được chọn để duyệt / từ chối */
    dsChon?: Array<number>;
    onSelectedChange?: (dsChon: Array<number>) => void;
}

const SELECT_ALL_VALUE = '__ALL__';

/**
 * Nhân viên trong dòng bổ sung đang chờ người duyệt cuối cùng duyệt
 */
export const laBoSungChoDuyet = (nhanVien: any): boolean => (nhanVien?.trangThai ?? trangThaiDangKyDaoTao.khoiTao) === trangThaiDangKyDaoTao.khoiTao;

/**
 * Bản tổng hợp chưa trình (đang khởi tạo, thu hồi hoặc bị trả lại về văn phòng)
 */
const laChuaTrinh = (trangThaiTongHop: number): boolean => [
    trangThaiTongHopDangKyDaoTao.khoiTao, trangThaiTongHopDangKyDaoTao.thuHoi,
    trangThaiTongHopDangKyDaoTao.phoCVPTraLai, trangThaiTongHopDangKyDaoTao.chanhVPTraLai,
    trangThaiTongHopDangKyDaoTao.phoGiamDocTraLai, trangThaiTongHopDangKyDaoTao.giamDocTraLai
].includes(trangThaiTongHop);

/**
 * Nhãn + màu trạng thái của 1 nhân viên trong dòng bổ sung
 */
const layTrangThaiNhanVien = (nhanVien: any, trangThaiTongHop: number): { nhan: string; mauTag: string; mauChu: string } => {
    if (nhanVien.trangThai === trangThaiDangKyDaoTao.lanhDaoQuyTuChoi) return { nhan: 'Từ chối', mauTag: 'error', mauChu: '#ff4d4f' };
    if (nhanVien.trangThai === trangThaiDangKyDaoTao.pheDuyet || trangThaiTongHop >= trangThaiTongHopDangKyDaoTao.pheDuyet) {
        return { nhan: 'Duyệt', mauTag: 'success', mauChu: '#52c41a' };
    }
    if (laChuaTrinh(trangThaiTongHop)) return { nhan: 'VP khởi tạo', mauTag: 'processing', mauChu: '#1677ff' };
    return { nhan: 'Chờ duyệt', mauTag: 'purple', mauChu: '#722ed1' };
};

const BoSungDangKyDaoTaoTable = ({ data, trangThai, idDangKyDaoTaoNam, isEditable, onReload, coTheDuyet, dsChon = [], onSelectedChange }: Props) => {
    const [openModal, setOpenModal] = useState(false);
    const [showMoLop, setShowMoLop] = useState<any>({ isShow: false, data: {} });
    const [loadingNhanVien, setLoadingNhanVien] = useState(false);
    const [dsNhanVien, setDsNhanVien] = useState<any[]>([]);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    // id chi tiết của các nhân viên đang chờ duyệt (được hiện ô chọn)
    const dsIdChonDuoc = useMemo(
        () => coTheDuyet
            ? (data || []).flatMap((x: any) => x.nhanViens || []).filter(laBoSungChoDuyet).map((x: any) => x.id)
            : [],
        [data, coTheDuyet]
    );
    const chonTatCa = dsIdChonDuoc.length > 0 && dsChon.length === dsIdChonDuoc.length;
    const chonNhanVien = (idChiTiet: number, isChon: boolean) => {
        onSelectedChange?.(isChon ? [...dsChon, idChiTiet] : dsChon.filter(x => x !== idChiTiet));
    };

    const optionsNhanVien = useMemo(() => {
        if (dsNhanVien.length === 0) return [];
        return [{ label: 'Chọn tất cả', value: SELECT_ALL_VALUE }, ...dsNhanVien];
    }, [dsNhanVien]);

    const handleChangeNhanVien = (values: any[]) => {
        if (!values?.includes(SELECT_ALL_VALUE)) return;
        const idsDaChon = values.filter((x: any) => x !== SELECT_ALL_VALUE);
        const tatCaIds = dsNhanVien.map((x: any) => x.value);
        form.setFieldsValue({
            idsNhanVien: idsDaChon.length === tatCaIds.length ? [] : tatCaIds
        });
    };

    const handleKhoiTao = async () => {
        try {
            dispatch(showLoadingScreen());
            await khoiTaoLopDaoTaoKeHoach(showMoLop.data);
            toast.success('Khởi tạo lớp đào tạo thành công');
            onReload();
            setShowMoLop({
                isShow: false,
                data: {}
            })
        } catch (e: any) {
            toast.error(e.message || 'Có lỗi xảy ra');
        } finally {
            dispatch(hideLoadingScreen());
        }
    };

    useEffect(() => {
        const loadNhanVien = async () => {
            setLoadingNhanVien(true);
            try {
                const req = new TimKiemNhanVienRequest();
                const rs = await timKiemDSNhanVien(req, 0, 0);
                if (rs && rs.data) {
                    setDsNhanVien(rs.data.map((x: any) => ({
                        label: `${x.hoTen} - ${x.tenDonVi}`,
                        value: x.id
                    })));
                }
            } catch (e) {

            }
            setLoadingNhanVien(false);
        };
        loadNhanVien();
    }, []);

    const handleAdd = () => {
        form.resetFields();
        form.setFieldsValue({ id: 0, idDangKyDaoTaoNam });
        setOpenModal(true);
    };

    const handleEdit = (record: any) => {
        form.setFieldsValue({
            id: record.id,
            idDangKyDaoTaoNam,
            chuyenDe: record.chuyenDe,
            noiDung: record.noiDung,
            coSoDaoTao: record.coSoDaoTao,
            duTruKinhPhi: record.duTruKinhPhi,
            thoiGianDuKien: record.thoiGianDuKien,
            idsNhanVien: record.nhanViens?.map((x: any) => x.idNhanVien) || []
        });
        setOpenModal(true);
    };

    const handleDelete = async (id: number) => {
        try {
            dispatch(showLoadingScreen());
            await xoaDangKyDaoTaoBoSung(id);
            toast.success('Xóa thành công');
            onReload();
        } catch (e: any) {
            toast.error(e.message || 'Có lỗi xảy ra');
        } finally {
            dispatch(hideLoadingScreen());
        }
    };

    const handleSave = async (values: any) => {
        try {
            dispatch(showLoadingScreen());
            await luuDangKyDaoTaoBoSung(values);
            toast.success('Lưu thành công');
            setOpenModal(false);
            onReload();
        } catch (e: any) {
            toast.error(e.message || 'Có lỗi xảy ra');
        } finally {
            dispatch(hideLoadingScreen());
        }
    };

    const columns = [
        {
            title: 'STT',
            width: 50,
            align: 'center' as const,
            render: (text: any, record: any, index: number) => index + 1,
        },
        {
            title: (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {dsIdChonDuoc.length > 0 && (
                        <Checkbox
                            title="Chọn tất cả"
                            checked={chonTatCa}
                            indeterminate={dsChon.length > 0 && !chonTatCa}
                            onChange={(e) => onSelectedChange?.(e.target.checked ? dsIdChonDuoc : [])}
                        />
                    )}
                    <span>Đối tượng</span>
                </div>
            ),
            dataIndex: 'nhanViens',
            render: (nhanViens: any[]) => (
                <>
                    {nhanViens?.map((nv, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                            {coTheDuyet && laBoSungChoDuyet(nv) && (
                                <Checkbox
                                    checked={dsChon.includes(nv.id)}
                                    onChange={(e) => chonNhanVien(nv.id, e.target.checked)}
                                />
                            )}
                            <span
                                title={layTrangThaiNhanVien(nv, trangThai).nhan}
                                style={{ fontWeight: 500, color: layTrangThaiNhanVien(nv, trangThai).mauChu }}
                            >{nv.hoTen}</span>
                        </div>
                    ))}
                </>
            )
        },
        {
            title: 'Chuyên đề đào tạo',
            dataIndex: 'chuyenDe',
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: 'Nội dung đào tạo',
            dataIndex: 'noiDung',
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: 'Cơ sở đào tạo',
            dataIndex: 'coSoDaoTao',
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: 'Dự trù kinh phí',
            dataIndex: 'duTruKinhPhi',
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: 'Thời gian đào tạo',
            dataIndex: 'thoiGianDuKien',
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: 'Phòng/Ban đăng ký',
            dataIndex: 'nhanViens',
            render: (nhanViens: any[]) => {
                const mapDonVi = new Map<string, number>();
                (nhanViens || []).forEach(nv => {
                    if (nv.tenDonVi) {
                        mapDonVi.set(nv.tenDonVi, (mapDonVi.get(nv.tenDonVi) || 0) + 1);
                    }
                });
                const dsDonVi = Array.from(mapDonVi.keys());
                return (
                    <>
                        {dsDonVi.map((tenDonVi, index) => {
                            const soNguoi = mapDonVi.get(tenDonVi);
                            return (
                                <div key={tenDonVi} className="text-enter">
                                    {tenDonVi} ({soNguoi}){index < dsDonVi.length - 1 ? "," : ""}
                                </div>
                            );
                        })}
                    </>
                );
            }
        },
        {
            title: 'Trạng thái',
            dataIndex: 'nhanViens',
            width: 140,
            align: 'center' as const,
            hidden: trangThai == trangThaiTongHopDangKyDaoTao.banHanh,
            render: (nhanViens: any[]) => {
                // đếm số nhân viên theo từng trạng thái (giữ thứ tự xuất hiện)
                const dsNhom = new Map<string, { mauTag: string; soLuong: number }>();
                (nhanViens || []).forEach(nv => {
                    const { nhan, mauTag } = layTrangThaiNhanVien(nv, trangThai);
                    const nhom = dsNhom.get(nhan);
                    dsNhom.set(nhan, { mauTag, soLuong: (nhom?.soLuong || 0) + 1 });
                });
                if (dsNhom.size === 0) return '-';
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {Array.from(dsNhom.entries()).map(([nhan, { mauTag, soLuong }]) => (
                            <Tag key={nhan} color={mauTag} style={{ margin: 2 }}>{nhan} ({soLuong})</Tag>
                        ))}
                    </div>
                );
            }
        },
        {
            title: 'Thao tác',
            align: 'center' as const,
            width: 100,
            hidden: !isEditable,
            render: (text: any, record: any) => (
                <Space>
                    <Button type="text" icon={<EditOutlined style={{ color: '#faad14' }} />} onClick={() => handleEdit(record)} />
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa dữ liệu này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Có"
                        cancelText="Không"
                        placement="topRight"
                    >
                        <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                    </Popconfirm>
                </Space>
            )
        },
        ...(trangThai == trangThaiTongHopDangKyDaoTao.banHanh ? [
            {
                title: "Trạng thái mở lớp",
                dataIndex: "trangThaiLopDaoTao",
                width: 120,
                align: "center" as const,
                render: (value, record) => {
                    return <TrangThaiLopDaoTaoTag value={value} />
                }
            },
            {
                title: "Thao tác",
                dataIndex: "idLopDaoTao",
                width: 110,
                align: "center" as const,
                render: (idLopDaoTao: number, record: any) => {
                    const daMoLop = record.trangThaiLopDaoTao > trangThaiLopDaoTao.chuaKhoiTao;

                    return daMoLop ? (
                        <Link title="Xem thông tin lớp đào tạo"
                            className="hrm-btn hrm-btn-sm btn-primary"
                            to={`${Links.DaoTao.Xem}?id=${idLopDaoTao}`}
                        >
                            Xem
                        </Link>
                    ) : (
                        <Button
                            className="hrm-btn-warning"
                            disabled={!(idLopDaoTao > 0)}
                            title={
                                idLopDaoTao > 0
                                    ? "Mở lớp đào tạo"
                                    : "Chưa có lớp đào tạo tương ứng"
                            }
                            onClick={() => {
                                setShowMoLop({
                                    isShow: true,
                                    data: {
                                        idLopDaoTao: idLopDaoTao,
                                        chuyenDe: record.chuyenDe,
                                        idNVTheoDoi: undefined
                                    }
                                })
                            }
                            }
                        >
                            Mở lớp
                        </Button>
                    );
                }
            }] : [])
    ].filter(x => !x.hidden);

    return (
        <div>
            {isEditable && (
                <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        Thêm dữ liệu bổ sung
                    </Button>
                </div>
            )}
            <Table
                bordered
                size="small"
                columns={columns}
                dataSource={data}
                rowKey="id"
                pagination={false}
                rowClassName={(record, index) => {
                    return record?.idLopDaoTao === showMoLop.data?.idLopDaoTao ? "selected-row row-key-" : "";
                }}
            />

            {openModal && (
                <ModalTextForm
                    show={openModal}
                    title={form.getFieldValue('id') ? "Cập nhật dữ liệu bổ sung" : "Thêm mới dữ liệu bổ sung"}
                    dialogClassName={"modal-width-60"}
                    handleClose={() => setOpenModal(false)}
                    handleSave={() => {
                        form.validateFields().then(values => {
                            handleSave(values);
                        }).catch(info => {
                            toast.error('Lỗi khi lưu dữ liệu bổ sung:', info);
                        });
                    }}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item name="id" hidden><Input.TextArea autoSize={{ minRows: 1 }} /></Form.Item>
                        <Form.Item name="idDangKyDaoTaoNam" hidden><Input.TextArea autoSize={{ minRows: 1 }} /></Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="chuyenDe" label="Chuyên đề đào tạo" rules={[{ required: true, message: 'Vui lòng nhập chuyên đề đào tạo' }]}>
                                    <Input.TextArea autoSize={{ minRows: 1 }} placeholder="Nhập chuyên đề" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="noiDung" label="Nội dung đào tạo" rules={[{ required: true, message: 'Vui lòng nhập nội dung đào tạo' }]}>
                                    <Input.TextArea autoSize={{ minRows: 1 }} placeholder="Nhập nội dung" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="coSoDaoTao" label="Cơ sở đào tạo">
                                    <Input.TextArea autoSize={{ minRows: 1 }} placeholder="Nhập cơ sở đào tạo" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="thoiGianDuKien" label="Thời gian dự kiến">
                                    <Input.TextArea autoSize={{ minRows: 1 }} placeholder="Nhập thời gian dự kiến" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="duTruKinhPhi" label="Dự trù kinh phí">
                                    <Input.TextArea autoSize={{ minRows: 1 }} placeholder="Nhập dự trù kinh phí" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="idsNhanVien"
                                    label="Cán bộ nhân viên"
                                    rules={[{ required: true, message: 'Vui lòng chọn cán bộ nhân viên' }]}
                                >
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        allowClear
                                        className="select-nhan-vien"
                                        filterOption={(input, option) => option?.value === SELECT_ALL_VALUE || (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                                        options={optionsNhanVien}
                                        loading={loadingNhanVien}
                                        placeholder="Chọn nhân viên"
                                        onChange={handleChangeNhanVien}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </ModalTextForm>
            )}

            <ModalTextForm
                show={showMoLop.isShow}
                title="Khởi tạo lớp đào tạo"
                width={850}
                handleClose={() =>
                    setShowMoLop({
                        isShow: false,
                        data: {}
                    })}
                handleSave={handleKhoiTao}
            >
                <Row gutter={[16, 16]}>
                    <Col span="24">
                        <span>Bạn có muốn khởi tạo lớp đào tạo <strong style={{ fontSize: 20 }}>{showMoLop.data?.chuyenDe}</strong>? Hãy chọn chuyên viên theo dõi và bấm nút <strong> Xác nhận</strong></span>
                    </Col>
                    <Col span="8">
                        <label>Chọn chuyên viên theo dõi: </label>
                    </Col>
                    <Col span="16">
                        <NhanVienSelectBox
                            theoQuyen="Theo dõi đào tạo"
                            value={showMoLop.data?.idNVTheoDoi}
                            onChange={(e: any) => {
                                setShowMoLop(prev => ({
                                    ...prev,
                                    data: {
                                        ...prev.data,
                                        idNVTheoDoi: e.target.value
                                    }
                                }));
                            }}
                        />
                    </Col>
                </Row>
            </ModalTextForm>
        </div>
    );
};

export default BoSungDangKyDaoTaoTable;
